import { useMemo, useCallback } from 'react';
import type { UserPresenceState } from '@annoteer/sync-layer';
import { useAwareness } from './useAwareness.js';
import { useSync } from '../contexts/SyncContext.js';
import { usePlatform } from '../contexts/PlatformContext.js';
import { dedupeViewers } from '../utils/viewers.js';

/**
 * Higher-level presence hook providing active user lists and page-level filtering.
 * Derives from the core useAwareness hook.
 */
export function usePresence() {
  const { remoteUsers, remoteCursors, setCurrentPage } = useAwareness();
  const { connectionState } = useSync();
  const { user } = usePlatform();
  const localUserId = user?.id;

  /**
   * The other PEOPLE in the room, one entry each — what a viewer count and an
   * avatar row are actually about.
   *
   * `remoteUsers` is a list of connections, and connections outnumber people:
   * a presenter who shares to the stage holds two, and a client that vanishes
   * without closing its socket lingers until awareness times it out. In a real
   * meeting on 2026-08-28 that read "3 viewers · NT MC MC" for two people.
   *
   * Kept separate from `remoteUsers` rather than replacing it, because the
   * page-relay in SidePanel needs to find our OWN stage client — the very
   * entry this list drops.
   */
  const viewers = useMemo(
    () => dedupeViewers(remoteUsers, localUserId),
    [remoteUsers, localUserId],
  );

  /** Number of other people in the room. */
  const userCount = viewers.length;

  /** Whether the local client is connected. */
  const isConnected = connectionState === 'connected';

  /** Get the other people currently viewing a specific page. */
  const getUsersOnPage = useCallback(
    (pageNumber: number): UserPresenceState[] => {
      return viewers.filter((u) => u.currentPage === pageNumber);
    },
    [viewers],
  );

  /**
   * Map of page number → list of people on that page.
   * Only includes pages with at least one other person.
   */
  const usersByPage = useMemo(() => {
    const map = new Map<number, UserPresenceState[]>();
    for (const user of viewers) {
      if (!user.currentPage) continue;
      const page = user.currentPage;
      const list = map.get(page);
      if (list) {
        list.push(user);
      } else {
        map.set(page, [user]);
      }
    }
    return map;
  }, [viewers]);

  return {
    /** All remote CONNECTIONS in the room — one person may hold several. */
    remoteUsers,
    /** The other people in the room, deduplicated. Use this for anything user-facing. */
    viewers,
    /** Remote cursors map (clientId → presence state). */
    remoteCursors,
    /** Number of other people in the room. */
    userCount,
    /** Whether the local client is connected to the sync server. */
    isConnected,
    /** Get the other people on a specific page. */
    getUsersOnPage,
    /** Map of page number → people on that page. */
    usersByPage,
    /** Update the local user's current page in awareness. */
    setCurrentPage,
  };
}
